<template>
    <layout-authenticated>
        <v-breadcrumbs :items="breadcrumb"></v-breadcrumbs>
        <h1 class="mb-3">{{ $t('shopping_cart.prepared_bulk_orders') }}</h1>
        <h3 class="grey--text mb-3">{{ $t('shopping_cart.shopping_carts_in_bulk_order') }}</h3>
        <v-card outlined class="mb-3" v-if="!isBulkOrdersEmpty">
            <bulk-orders-table/>
        </v-card>
        <div v-else>
            <v-alert icon="mdi-information" prominent text type="info">
                {{ $t('shopping_cart.bulk_orders_empty') }}
            </v-alert>
        </div>
        <h3 class="grey--text mb-3">{{ $t('shopping_cart.orders_grouped_by_product_sets') }}</h3>
        <bulk-order-summary-component class="mb-3" v-if="!isBulkOrdersEmpty"/>
        <div v-else>
            <v-alert icon="mdi-information" prominent text type="info">
                {{ $t('shopping_cart.bulk_orders_empty') }}
            </v-alert>
        </div>
        <h3 class="grey--text mb-3">{{ $t('shopping_cart.message') }}</h3>
        <v-form>
            <v-text-field
                v-model="form.name"
                :label="$t('shopping_cart.name')"
                :error-messages="nameErrors"
                outlined
                dense
                @input="$v.form.name.$touch()"
                @blur="$v.form.name.$touch()"
            />
            <v-textarea
                v-model="form.message"
                :label="$t('shopping_cart.message')"
                :error-messages="messageErrors"
                outlined
                dense
                @input="$v.form.message.$touch()"
                @blur="$v.form.message.$touch()"
            />
        </v-form>
        <div class="d-flex justify-end">
            <v-btn color="primary" @click="onBulkOrderConfirm" :disabled="isBulkOrdersEmpty || isBusy" :loading="isBusy">
                <v-icon left>mdi-format-list-checkbox</v-icon>
                {{ $t('shopping_cart.submit_bulk_order') }}
            </v-btn>
        </div>
    </layout-authenticated>
</template>

<script>
import BulkOrdersTable from '@/components/user/shoppingCart/BulkOrdersTable'
import ShoppingCartMixin from '@/mixins/ShoppingCartMixin'
import SnackbarMixin from '@/mixins/SnackbarMixin'
import BulkOrderMixin from '@/mixins/BulkOrderMixin'
import ProductSetMixin from '@/mixins/ProductSetMixin'
import BulkOrderSummaryComponent from '@/components/user/bulkOrder/BulkOrderSummaryComponent'
import moment from "moment";
import {validationMixin} from 'vuelidate'
import {required} from 'vuelidate/lib/validators'
import {API} from '@/api/api'
import LayoutAuthenticated from "@/layouts/LayoutAuthenticated";
import {
    aggregateProducts,
    findUnavailableProducts,
    formatUnavailableProductsAlert,
    snapshotProducts,
    snapshotRecipients,
    snapshotValue
} from "@/utils/orderAvailability";

export default {
    name: "BulkOrders",
    mixins: [ShoppingCartMixin, SnackbarMixin, BulkOrderMixin, validationMixin, ProductSetMixin],
    components: {LayoutAuthenticated, BulkOrderSummaryComponent, BulkOrdersTable},
    data: () => ({
        // loading
        isBusySubmit: false,
        isBusyCheckAvailability: false,
        // form
        form: {
            name: "Rozesílka " + moment().format('LLL'),
            message: ""
        }
    }),
    validations: {
        form: {
            name: {required},
            message: {}
        }
    },
    computed: {
        isBusy() {
            return this.isBusySubmit || this.isBusyCheckAvailability;
        },
        isBulkOrdersEmpty() {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            return this.getNumberOfBulkOrders(userId, warehouseId) === 0;
        },
        breadcrumb() {
            return [
                {
                    text: this.$t('app.name'),
                    href: '/'
                },
                {
                    text: this.$t('orders.bulk_orders'),
                    href: '/bulk-orders',
                    disabled: true
                }
            ];
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.form.name.$dirty) return errors;
            !this.$v.form.name.required && errors.push(this.$t('validation.name.required'));
            return errors;
        },
        messageErrors() {
            return [];
        }
    },
    methods: {
        async onBulkOrderConfirm() {
            if (this.isBusy) {
                return;
            }

            this.$v.form.$touch();
            if (this.$v.form.$anyError) {
                return;
            }

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            this.isBusyCheckAvailability = true;
            try {
                const productSets = snapshotValue(this.productSets);
                const name = this.form.name;
                const message = this.form.message;
                const shoppingCartData = JSON.stringify(snapshotValue(
                    this.$store.getters["bulkOrder/getData"]
                ));
                const orders = [];

                productSets.forEach(item => {
                    const products = snapshotProducts(Object.values(item.productSet));
                    const recipients = snapshotRecipients(item.recipients);
                    const items = Object.freeze(products.map(product => Object.freeze({
                        name: product.name,
                        external_id: product.external_id,
                        brand: product.brand,
                        model: product.model,
                        image_url: product.image_url,
                        quantity: product.quantity
                    })));

                    recipients.forEach(recipient => {
                        orders.push(Object.freeze({
                            buyer_external_id: recipient.buyer_id,
                            address_external_id: recipient.address_id,
                            phone: recipient.phone,
                            email: recipient.email,
                            warehouse_id: warehouseId,
                            items,
                            message
                        }));
                    });
                });

                const orderSnapshot = Object.freeze(orders);
                const data = Object.freeze({
                    name,
                    warehouse_id: warehouseId,
                    shopping_cart_data: shoppingCartData,
                    orders: orderSnapshot
                });
                const availability = aggregateProducts(
                    orderSnapshot.map(order => order.items)
                );
                const unavailableProducts = await findUnavailableProducts(
                    warehouseId,
                    availability,
                    API
                );
                if (unavailableProducts.length > 0) {
                    window.alert(formatUnavailableProductsAlert(
                        this.$t("summary.check_availability_failure"),
                        unavailableProducts
                    ));
                    return;
                }

                this.isBusySubmit = true;
                this.isBusyCheckAvailability = false;
                await API.post("/api/bulk-orders", data);
                this.removeOrdersFromBulkOrders(userId, warehouseId);
                this.snackbarSuccess(this.$t('snackbar.bulk_order.created'));
                this.$router.push({name: 'orders'});
            } catch (error) {
                this.snackbarError(
                    typeof error === "string"
                        ? error
                        : (error.message || this.$t("snackbar.unknown_error"))
                );
            } finally {
                this.isBusyCheckAvailability = false;
                this.isBusySubmit = false;
            }
        }
    }
}
</script>
